import { el, tel, StatusLine } from "./dom.ts";
import { t, onLangChange } from "./i18n.ts";
import { signIn, signOut, getAccount } from "../auth/broker.ts";
import { setState, subscribe } from "./state.ts";
import { createSection, type Section } from "./section.ts";
import { SECTION_ICON } from "./icons.ts";

/**
 * Sign-in panel.
 *
 * Requests only openid/profile/email/offline_access/User.Read — module scopes
 * are approved separately once the user has said what they want to export, so
 * the first prompt stays trivial (ARCHITECTURE.md §4).
 */

function isUserCancellation(err: unknown): boolean {
    // MSAL reports a closed popup as BrowserAuthError with one of these codes.
    const code = (err as { errorCode?: string } | null)?.errorCode;
    return code === "user_cancelled" || code === "popup_window_error";
}

function message(err: unknown): string {
    if (err instanceof Error && err.message) {
        return err.message;
    }
    return t("error.unknown");
}

export function createAuthPanel(): Section {
    const panel = createSection({
        id: "panel-auth",
        titleKey: "auth.section",
        introKey: "auth.consentNote",
        icon: SECTION_ICON.auth
    });

    const status = new StatusLine();

    const signInButton = tel("button", "auth.signIn", { type: "button", class: "primary" });
    const signOutButton = tel("button", "auth.signOut", { type: "button" });

    panel.body.append(el("div", { class: "actions" }, [signInButton, signOutButton]), status.node);

    const render = (account: ReturnType<typeof getAccount>) => {
        const signedIn = account !== null;
        signInButton.hidden = signedIn;
        signOutButton.hidden = !signedIn;

        if (signedIn) {
            const name = account.name ?? account.username;
            status.set(t("auth.signedInAs", { name }), "ok");
            panel.setState(true, name);
        } else {
            status.set(t("auth.notSignedIn"), "muted");
            panel.setState(false, t("auth.notSignedInSummary"));
        }
    };

    signInButton.addEventListener("click", async () => {
        signInButton.disabled = true;
        status.set(t("auth.signingIn"), "muted");
        try {
            const account = await signIn();
            setState({ account });
        } catch (err) {
            if (isUserCancellation(err)) {
                status.set(t("auth.cancelled"), "warn");
            } else {
                status.set(t("auth.error", { message: message(err) }), "err");
            }
        } finally {
            signInButton.disabled = false;
        }
    });

    signOutButton.addEventListener("click", async () => {
        signOutButton.disabled = true;
        try {
            await signOut();
            // Clear derived state too: leaving granted modules behind would
            // have later panels claim progress that no longer applies to a
            // signed-out account.
            setState({ account: null, grantedModules: [] });
        } catch (err) {
            if (!isUserCancellation(err)) {
                status.set(t("auth.error", { message: message(err) }), "err");
            }
        } finally {
            signOutButton.disabled = false;
        }
    });

    subscribe((state) => render(state.account));
    onLangChange(() => render(getAccount()));
    return panel;
}
